import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FiChecklist2ReportWidgetBaseDirective } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-widget-base.directive';
import {
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportDateRange,
    IChecklist2ReportHistoryComplianceData,
    IChecklistHistoryDateRangeChangeEvent,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiChecklist2ReportHistoryService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-history.service';
import { IReportWidget } from '@fi-shared/fi-report-widget/fi-report-widget';
import { IChecklistStatusProgressData } from '@fi-shared/checklist/checklist-v2';
import { INameIdObj } from '@fi-app/shared/fi-shared-interfaces';
import { FiChecklist2ReportTimePeriodType } from '../../../enums/fi-checklist2-report-time-period-type';

@Component({
    selector: 'fi-history-widget',
    templateUrl: './history-widget.component.html',
    styleUrls: ['./history-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-widget' },
    standalone: false,
})
export class FiChecklist2ReportHistoryWidget
    extends FiChecklist2ReportWidgetBaseDirective
    implements OnInit, OnDestroy
{
    @Output() checklistHistoryDateRangeChange = new EventEmitter<IChecklistHistoryDateRangeChangeEvent>();

    protected checklistHistoryDateRange: IChecklist2ReportDateRange;
    private filteredChecklistReportSummary: IChecklist2ReportChecklistSummary[] = [];
    protected widgets: IReportWidget[];
    protected readonly FiChecklist2ReportTimePeriodType = FiChecklist2ReportTimePeriodType;
    protected get loading$() {
        return this.fiChecklist2ReportDataService.getLoading$(this.reportParameter.Id);
    }

    constructor(
        injector: Injector,
        private fiChecklist2ReportHistoryService: FiChecklist2ReportHistoryService,
    ) {
        super(injector);
    }
    checklistWidgetHistoryOptions: INameIdObj[] = [];

    public ngOnInit() {
        super.ngOnInit();
        this.checklistWidgetHistoryOptions = this.fiChecklist2ReportHelperService.getChecklistReportHistoryOptions();

        this.fiChecklist2ReportDataService.resetDateRange(this.reportParameter.Id);
        this.checklistHistoryDateRange = this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id);
        this.setNewHistorySummaryData(this.reportData.getHistorySummary(this.reportParameter.Id));
        this.susbcribeOnDateRangeChanged();
        this.setWidgetsData();
    }

    private susbcribeOnDateRangeChanged(): void {
        this.fiChecklist2ReportDataService.subscribeOnDateRangeChanged(this.reportParameter.Id, this.ngOnDestroy$, () =>
            this.updateHistorySummary(),
        );
    }

    private updateHistorySummary(): void {
        const dateRange = this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id);
        this.fiChecklist2ReportHistoryService
            .getChecklistHistorySummary(this.currentFilter, dateRange, this.reportData.auditMode)
            .then((data) => {
                this.setNewHistorySummaryData(data);
            });
    }

    private setWidgetsData(): void {
        this.widgets = [
            {
                icon: 'far fa-clock-rotate-left',
                description: this.getWidgetDescription(),
                summaryCount: this.getChecklistSummaryUnitsCount(),
            },
            {
                name: this.appTextService.get('checklist.report_non_compliance_status'),
                icon: 'far fa-badge-check',
                description: this.appTextService.get('checklist.report_non_compliant_checklists_percentage'),
                summaryCount: this.getNonComplianceChecklistsPercentage(),
                isHidden: () => !this.isNonComplianceChartVisible(),
            },
        ];
    }

    protected nonComplianceData$ = new BehaviorSubject<IChecklist2ReportHistoryComplianceData>(undefined);
    private setNewHistorySummaryData(historySummaryData: IChecklist2ReportChecklistSummary[]) {
        this.filteredChecklistReportSummary = historySummaryData;
        const nonComplianceDataFromSummary = this.fiChecklist2ReportHelperService.getNonComplianceDataFromSummary(
            this.filteredChecklistReportSummary,
        );
        this.nonComplianceData$.next(nonComplianceDataFromSummary);
    }

    protected getWidgetDescription(): string {
        return (
            this.reportParameter.Description || this.appTextService.get('checklist.report_past_checklists_total_number')
        );
    }

    protected isNonComplianceChartVisible(): boolean {
        const nonComplianceData = this.nonComplianceData$.value;
        const totalUnitsCount = nonComplianceData.nonComplianceUnitsCount + nonComplianceData.complianceUnitsCount;
        return totalUnitsCount > 0;
    }

    private getNonComplianceChecklistsPercentage(): number {
        const nonComplianceData = this.nonComplianceData$.value;
        return nonComplianceData.iterationsCount > 0
            ? Math.round((100 * nonComplianceData.nonComplianceUnitsCount) / nonComplianceData.iterationsCount)
            : 0;
    }

    private getChecklistSummaryUnitsCount(): number {
        return this.fiChecklist2ReportHelperService.getChecklistSummaryTotalUnitsCount(
            this.filteredChecklistReportSummary,
        );
    }

    protected setChecklistHistoryDateRange(newHistoryDateRange: IChecklist2ReportDateRange) {
        this.fiChecklist2ReportDataService.setNewDateRange(this.reportParameter.Id, newHistoryDateRange);
    }

    protected getStatusProgressData(): IChecklistStatusProgressData {
        return this.fiChecklist2ReportHelperService.getStatusProgressDataFromSummary(
            this.filteredChecklistReportSummary,
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
